// Stacks the graffiti SVG and its ASCII rendering, opening on the ASCII once
// it renders, or on the SVG if it cannot. A lens that follows the pointer
// reveals the view underneath, and clicking floods the lens to swap.
// The play button animates the view on show: a laser etches the SVG in, and
// falling code writes the ASCII.

import { AsciiDisplay } from "./ascii/display";
import { Etcher } from "./etch/etcher";
import { Lens, type Point } from "./lens";
import { Rain } from "./rain/rain";

/**
 * Wires up the artwork and toolbar inside a container holding index.html's
 * markup. The ASCII is sized to the container's width, and the code rain
 * fills the container.
 * Returns a function that removes the listeners, stops the animations and
 * observers, and leaves any work still under way from changing the markup.
 */
export const mountSite = (container: HTMLElement): (() => void) => {
  const unmounted = new AbortController();
  const { signal } = unmounted;
  const mounted = () => !signal.aborted;

  const find = <T extends Element>(selector: string): T => {
    const element = container.querySelector<T>(selector);
    if (!element) throw new Error(`Missing ${selector}`);
    return element;
  };

  const stage = find<HTMLElement>(".stage");
  const graffiti = find<HTMLImageElement>(".graffiti");
  const ascii = find<HTMLPreElement>(".ascii");
  const toggle = find<HTMLButtonElement>(".view-toggle");
  const play = find<HTMLButtonElement>(".play-toggle");

  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  const lens = new Lens(stage, reducedMotion);
  const display = new AsciiDisplay(
    ascii,
    () => container.clientWidth,
    () => settle(),
  );
  const etcher = new Etcher(stage, graffiti);
  const rain = new Rain(container, stage, ascii, display);

  let pointer: Point | undefined;
  let flooding = false;

  const animating = () => etcher.playing || rain.playing;
  const stopAnimating = () => {
    etcher.stop();
    rain.stop();
  };

  // The lens stays shut while an animation plays over the artwork.
  const settle = () => {
    if (!flooding && mounted()) {
      lens.peek(pointer !== undefined && display.ready && !animating());
    }
  };

  const shown = () => stage.dataset.view === "ascii";
  const wanted = () => toggle.getAttribute("aria-pressed") === "true";

  // The play button names the animation of the view the toggle picks.
  const labelPlay = () => {
    const action = play.dataset.playing === undefined ? "Play" : "Stop";
    const text = `${action} ${wanted() ? "code rain" : "etching"} animation`;
    play.setAttribute("aria-label", text);
    play.title = text;
  };

  const show = async (asAscii: boolean, origin?: Point): Promise<void> => {
    toggle.setAttribute("aria-pressed", String(asAscii));
    labelPlay();
    // The SVG waits hidden only while the page opens on the ASCII; choosing
    // the SVG shows it at once. See index.html.
    if (!asAscii) stage.dataset.ready = "";
    try {
      if (asAscii) await display.draw();
    } catch (error) {
      if (!mounted()) return;
      console.error(error);
      return show(false);
    }
    if (!mounted()) return;
    // A later click wins over a render that was still in flight. A flood that
    // is already under way checks which view is wanted once it ends, so it is
    // left to finish rather than interrupted by a second one.
    if (wanted() !== asAscii || shown() === asAscii || flooding) return;
    if (origin) {
      flooding = true;
      const flooded = await lens.flood(origin);
      flooding = false;
      if (!mounted()) return;
      if (!flooded || wanted() !== asAscii) return settle();
    }
    stage.dataset.view = asAscii ? "ascii" : "svg";
    graffiti.ariaHidden = asAscii ? "true" : null;
    ascii.ariaHidden = asAscii ? null : "true";
    // The view that was on top is underneath now; reopen the lens onto it.
    lens.snap(0);
    if (pointer) lens.aim(pointer);
    settle();
  };

  stage.addEventListener(
    "pointermove",
    (event) => {
      // Touch has no hover, so a tap flips the view without peeking first.
      if (event.pointerType === "touch") return;
      pointer = lens.locate(event);
      if (flooding) return;
      lens.aim(pointer);
      settle();
    },
    { signal },
  );
  stage.addEventListener(
    "pointerleave",
    () => {
      pointer = undefined;
      settle();
    },
    { signal },
  );
  stage.addEventListener(
    "click",
    (event) => {
      if (!flooding && !animating()) show(!shown(), lens.locate(event));
    },
    { signal },
  );

  // The container follows the viewport on the site, and its window when the
  // site is embedded in one.
  let resizeTimer: ReturnType<typeof setTimeout> | undefined;
  const resized = new ResizeObserver(() => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      if (display.requested) display.draw().catch(console.error);
    }, 200);
  });
  resized.observe(container);
  toggle.addEventListener(
    "click",
    () => {
      stopAnimating();
      show(!wanted());
    },
    { signal },
  );
  toggle.hidden = false;

  // Counts the animations started, so a stopped one settling late leaves the
  // button to the one that replaced it.
  let plays = 0;
  play.addEventListener(
    "click",
    async () => {
      if (animating()) return stopAnimating();
      if (flooding) return;
      // The etching draws the SVG, and the code rain writes the ASCII, so each
      // waits for its view to be on show.
      const asAscii = wanted();
      if (shown() !== asAscii) await show(asAscii);
      if (!mounted() || shown() !== asAscii || animating()) return;
      const current = ++plays;
      play.dataset.playing = "";
      labelPlay();
      const playing = (asAscii ? rain : etcher).play();
      settle();
      try {
        await playing;
      } catch (error) {
        console.error(error);
      } finally {
        if (mounted() && current === plays) {
          delete play.dataset.playing;
          labelPlay();
          settle();
        }
      }
    },
    { signal },
  );
  play.hidden = false;
  // The SVG stays hidden until the first view is settled.
  show(true).finally(() => {
    if (mounted()) stage.dataset.ready = "";
  });

  return () => {
    unmounted.abort();
    resized.disconnect();
    clearTimeout(resizeTimer);
    display.abandon();
    stopAnimating();
    // The animations end without settling, so their button resets here.
    delete play.dataset.playing;
    labelPlay();
    lens.snap(0);
  };
};
